'use client';

import type { IntlShape } from 'react-intl';
import { Array, Cause, Option } from 'effect';
import { useEffect } from 'react';
import { useIntl } from 'react-intl';

import { isViolationError } from '@vs-shop/engine/data/error/backendApi/_internal/createBackendApiError';
import { CartError } from '@vs-shop/engine/capability/cart/errors';
import { showErrorToast, showWarningToast } from '@vs-shop/ui/toast';
import { captureCause } from '../../../../shared/utils/sentry';

import type { RejectedCartLine } from '../../../../effect/atom/order/cartMutations';
import { rejectedCartLinesAtom } from '../../../../effect/atom/order/cartMutations';
import { MaxOrderableInCartError } from '../../../../effect/data/error/orderClient';
import { showViolationToast } from '../../../../utils/error/showViolationToast';
import { useAtom } from '@effect/atom-react';

const announce = ({ line, cause }: RejectedCartLine, intl: IntlShape) => {
  captureCause(cause, {
    component: 'CartRejectionAnnouncer',
    action: 'Add product to cart failed',
    additionalData: { productCode: line.variantCode, quantity: line.quantity },
  });

  const error = Option.map(
    Array.findFirst(cause.reasons, Cause.isFailReason),
    (reason) => reason.error
  );
  const maxOrderable = Option.filter(error, (u) => u instanceof MaxOrderableInCartError);
  const violation = Option.flatMap(
    Option.filter(error, isViolationError),
    (violationError) => violationError.firstViolation
  );
  const cartRejectionMessage = Option.flatMap(
    Option.filter(error, (u) => u instanceof CartError.Rejected),
    (rejection) => Array.head(rejection.messages)
  );

  if (Option.isSome(maxOrderable)) {
    showWarningToast(
      intl.formatMessage({
        id: 'error.orderMutation.maxOrderableInCart',
        defaultMessage: 'Váš košík již obsahuje maximální skladové množství položky.',
      })
    );
    return;
  }

  if (Option.isSome(violation)) {
    showViolationToast(violation.value, intl);
    return;
  }

  if (Option.isSome(cartRejectionMessage)) {
    showErrorToast(cartRejectionMessage.value);
    return;
  }

  showErrorToast(
    intl.formatMessage(
      {
        id: 'cart.addToCart.failed',
        defaultMessage:
          'Produkt {productName} se nepodařilo přidat do košíku. Zkuste to prosím znovu.',
      },
      { productName: line.productName }
    )
  );
};

/**
 * Announces each line the backend refused, once, and forgets it. The add
 * command runs detached from the button that started it, so the announcement
 * lives next to the drawer rather than in the button.
 */
export default function CartRejectionAnnouncer() {
  const intl = useIntl();
  const [rejected, setRejected] = useAtom(rejectedCartLinesAtom);

  useEffect(() => {
    if (rejected.length === 0) {
      return;
    }

    for (const rejection of rejected) {
      announce(rejection, intl);
    }
    setRejected((current) => Array.filter(current, (rejection) => !rejected.includes(rejection)));
  }, [rejected, intl, setRejected]);

  return null;
}
